import { type ErrorResponse, fetchApi } from '@ecomm/data-simplisafe-api'
import { type TypeOf, z } from '@simplisafe/ewok'
import { safeProp } from '@simplisafe/monda'
import { getDeviceType } from '@simplisafe/ss-ecomm-data/utils/windowScreenSize'
import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'

export type PackageComponents = {
  readonly auxiliarySiren?: number
  readonly bellBox?: number
  readonly carbonMonoxideDetector?: number
  readonly entrySensor?: number
  readonly freezeSensor?: number
  readonly glassbreakSensor?: number
  readonly keyFob?: number
  readonly keypad?: number
  readonly motionSensor?: number
  readonly outdoorCamera?: number
  readonly panicButton?: number
  readonly simplicam?: number
  readonly smokeDetector?: number
  readonly videoDoorbellPro?: number
  readonly waterSensor?: number
  readonly indoorCam?: number
}

const leadGenRecommendationsV2ResponseSchema = z.object({
  attributeHash: z.string(),
  email: z.string().nullable(),
  externalId: z.string().nullable(),
  leadId: z.string().nullable(),
  leadSource: z.string().nullable(),
  leadStatus: z.number(),
  message: z.string(),
  ['return_code']: z.number(),
  uid: z.string().optional()
})

export type LeadGenRecommendationsV2Response = TypeOf<
  typeof leadGenRecommendationsV2ResponseSchema
>

export type LeadGenRecommendationsV2Params = {
  readonly leadPromoOffer?: string | null
  readonly leadSourceVersion?: Record<string, unknown>
  readonly components: PackageComponents
  readonly email: string
}

const LEAD_SOURCE_TYPE_GSB = 'guided_system_builder'

/**
 * Creates the value for source to insert into leads table entry
 * @returns
 */
export const getLeadSourceGSB = () =>
  getDeviceType() === 'Mobile'
    ? 'Guidedsystembuilder_mobile'
    : 'Guidedsystembuilder_desktop'

export const buildRecommendationsBodyV2 = (
  params: LeadGenRecommendationsV2Params
) => {
  const utmParams = new URLSearchParams(globalThis.window?.location.search)
  return {
    components: params.components,
    email: params.email,
    ['lead_promo_offer']: safeProp('leadPromoOffer', params).orNull(),
    ['lead_source_details']: 'null',
    ['lead_source_type']: LEAD_SOURCE_TYPE_GSB,
    ['lead_source_version']: JSON.stringify(params.leadSourceVersion || {}),
    ['lead_type']: 'null',
    locale: process.env['LOCALE'],
    source: getLeadSourceGSB(),
    ['url_path']: globalThis.window?.location.pathname || '',
    ['utm_campaign']: utmParams.get('utm_campaign') || null,
    ['utm_medium']: utmParams.get('utm_medium') || null,
    ['utm_source']: utmParams.get('utm_source') || null
  }
}

export const leadGenRecommendationsV2 = (
  request: LeadGenRecommendationsV2Params
): TE.TaskEither<Error | ErrorResponse, LeadGenRecommendationsV2Response> => {
  return pipe(
    TE.Do,
    TE.chain(() => {
      return fetchApi({
        endpoint: `/leads/v1/quote-wizard/recommendation`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify(buildRecommendationsBodyV2(request))
      })
    }),
    TE.chain(response => {
      const validation =
        leadGenRecommendationsV2ResponseSchema.safeParse(response)
      return validation.success
        ? TE.right(validation.data)
        : TE.left(
            Error(
              `Invalid response structure: ${JSON.stringify(validation.error.format())}`
            )
          )
    }),
    TE.mapLeft(res =>
      Error(
        `Error fetching leadGenRecommendationsV2 method: ${JSON.stringify(res)}`
      )
    )
  )
}
